import { useState, useEffect } from 'react'
import { AlertTriangle, Loader2, CheckCircle2, User } from 'lucide-react'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { adminApi } from '@/services/api/admin'

interface TeacherOption {
  id: string
  first_name: string
  last_name: string
  email: string
  teacher_code: string | null
}

interface UnmatchedTeacherCodeDialogProps {
  isOpen: boolean
  unmatchedCodes: string[]
  onAssign: (code: string, teacherId: string) => void
  onSkip: () => void
}

export function UnmatchedTeacherCodeDialog({ isOpen, unmatchedCodes, onAssign, onSkip }: UnmatchedTeacherCodeDialogProps) {
  const [teachers, setTeachers] = useState<TeacherOption[]>([])
  const [loading, setLoading] = useState(false)
  const [assigning, setAssigning] = useState<string | null>(null)
  const [selectedTeacher, setSelectedTeacher] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      loadTeachers()
      setSelectedTeacher({})
      setError(null)
      setSuccess(null)
    }
  }, [isOpen])

  const loadTeachers = async () => {
    setLoading(true)
    try {
      const res = await handleApiRequest<{ teachers: TeacherOption[] }>(
        apiClient.get('/api/v1/admin/teachers?limit=500')
      )
      setTeachers(res.teachers || [])
    } catch {
      setError('Lehrer konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }

  const handleAssign = async (code: string) => {
    const teacherId = selectedTeacher[code]
    if (!teacherId) {
      setError('Bitte wählen Sie einen Lehrer aus')
      return
    }

    setAssigning(code)
    setError(null)
    try {
      await adminApi.assignTeacherCode(teacherId, code)
      setSuccess(`Code "${code}" wurde zugewiesen`)
      onAssign(code, teacherId)
    } catch (err: any) {
      const detail = err.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Zuweisung fehlgeschlagen')
    } finally {
      setAssigning(null)
    }
  }

  const handleSkip = () => {
    onSkip()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
          <h2 className="text-lg font-bold">Lehrer-Code nicht zugeordnet</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Folgende Lehrer-Codes wurden im PDF gefunden, aber keinem Lehrer zugeordnet:
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded text-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> {success}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-blue-500 mr-2" />
            <span className="text-gray-500 text-sm">Lehrer laden...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {unmatchedCodes.map(code => (
              <div key={code} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="font-mono font-bold text-lg">{code}</span>
                </div>

                <div className="flex gap-2">
                  <select
                    value={selectedTeacher[code] || ''}
                    onChange={(e) => setSelectedTeacher(prev => ({ ...prev, [code]: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="">-- Lehrer auswählen --</option>
                    {teachers
                      .filter(t => !t.teacher_code || t.teacher_code === code)
                      .sort((a, b) => a.last_name.localeCompare(b.last_name))
                      .map(t => (
                        <option key={t.id} value={t.id}>
                          {t.last_name}, {t.first_name} {t.email ? `(${t.email})` : ''}
                          {t.teacher_code ? ` [${t.teacher_code}]` : ' (kein Code)'}
                        </option>
                      ))}
                  </select>

                  <button
                    onClick={() => handleAssign(code)}
                    disabled={!selectedTeacher[code] || assigning === code}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {assigning === code ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Zuordnen'
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-between">
          <button
            onClick={handleSkip}
            className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm"
          >
            Seite überspringen
          </button>
          <button
            onClick={handleSkip}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
          >
            Schliessen
          </button>
        </div>
      </div>
    </div>
  )
}
